import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button, { type ButtonProps } from '@mui/material/Button';
import { neutralActionSx } from '@/theme/neutral-action.styles';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Semantic color for the confirm button — e.g. 'error' for a destructive action, so it
   * matches the danger color already used elsewhere for that same action (a red delete icon
   * confirmed by a primary-colored button reads as inconsistent). Defaults to 'primary'. */
  confirmColor?: ButtonProps['color'];
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmColor = 'primary',
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      {description && (
        <DialogContent>
          <DialogContentText>{description}</DialogContentText>
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={isLoading} variant="contained" sx={neutralActionSx}>
          {cancelLabel ?? t('common.cancel')}
        </Button>
        <Button onClick={onConfirm} loading={isLoading} variant="contained" color={confirmColor}>
          {confirmLabel ?? t('common.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
